import {DataStatus} from 'platform/components';

import {isNotNil} from 'ramda';

import {useLazyCalculateFromWithoutVatQuery} from '@dms/api/core';
import {
  EarningCategoryEnum,
  OneTimePrice,
  useGetVehicleEarningQuery,
  useListVehicleEarningCategoriesQuery,
  useUpdateVehicleEarningMutation,
} from '@dms/api/vehicle';
import i18n from '@dms/i18n';

import {DeepNonNullableWithPartial, RequiredTestIdProps} from 'shared/web';

import {useTenant} from '../../hooks/useTenant';
import {handleApiError} from '../../utils/handleApiError';
import {useSubscribeToFormChanges} from '../VehicleTransactionForm/hooks/useSubscribeToFormChanges';
import {
  VehicleTransactionForm,
  VehicleTransactionFormRef,
} from '../VehicleTransactionForm/VehicleTransactionForm';
import {VatCodePrice} from '../VehicleTransactionForm/VehicleTransactionFormSchema';

type FullFormSubmitHandler = Parameters<typeof VehicleTransactionForm>[0]['onSubmit'];

interface VehicleEditEarningDialogProps extends RequiredTestIdProps {
  vehicleId: string;
  earningId: string;
  onSuccess?: (id: string) => void;
  onDiscard?: VoidFunction;
}

export function VehicleEditEarningForm(props: VehicleEditEarningDialogProps) {
  const {
    data: earning,
    isLoading: isEarningLoading,
    isError: hasEarningError,
  } = useGetVehicleEarningQuery({earningId: props.earningId, vehicleId: props.vehicleId});
  const [updateVehicleEarning] = useUpdateVehicleEarningMutation();

  const {data: earningCategories} = useListVehicleEarningCategoriesQuery({});

  const {tenantCountry, tenantCurrency} = useTenant();

  const [calculatePriceWithoutVat] = useLazyCalculateFromWithoutVatQuery();

  const formRef = useSubscribeToFormChanges<VehicleTransactionFormRef>({
    watchFields: ['category'],
    onChange: (value, {formApi, formValues}) => {
      if (value.category?.[0] === 'FINANCING') {
        formApi?.setValue('expectedPrice.vatType', 'Z', {
          shouldDirty: true,
        });
        formApi?.setValue('realPrice.vatType', 'Z', {
          shouldDirty: true,
        });
        const expectedPrice = formValues.expectedPrice?.withoutVat?.amount;
        const realPrice = formValues.realPrice?.withoutVat?.amount;
        if (isNotNil(expectedPrice)) {
          calculatePriceWithoutVat({
            vatRateType: 'Z',
            amount: expectedPrice,
            countryCode: tenantCountry,
          })
            .unwrap()
            .then((response) => {
              formApi?.setValue('expectedPrice.withVat.amount', response.withVat, {
                shouldDirty: true,
              });
            })
            .catch(handleApiError);
        }
        if (isNotNil(realPrice)) {
          calculatePriceWithoutVat({
            vatRateType: 'Z',
            amount: realPrice,
            countryCode: tenantCountry,
          })
            .unwrap()
            .then((response) => {
              formApi?.setValue('realPrice.withVat.amount', response.withVat, {
                shouldDirty: true,
              });
            })
            .catch(handleApiError);
        }
      }
    },
  });

  const onSubmitFullForm: FullFormSubmitHandler = async (data) => {
    const {category, realPrice, expectedPrice, name, note} = data as DeepNonNullableWithPartial<
      typeof data
    >;

    const transformedExpectedPrice = transformToOneTimePrice(expectedPrice);
    const transformedRealPrice = transformToOneTimePrice(realPrice);

    await updateVehicleEarning({
      vehicleId: props.vehicleId,
      earningId: props.earningId,
      updateVehicleEarningRequestBody: {
        name,
        category: (category?.[0] as EarningCategoryEnum) ?? null,
        note: note ?? null,
        expectedPrice: transformedExpectedPrice,
        realPrice: transformedRealPrice,
        fileId: data.fileId ?? null,
      },
    })
      .unwrap()
      .then(() => {
        props.onSuccess?.(props.earningId);
      })
      .catch(handleApiError);
  };

  return (
    <DataStatus isLoading={isEarningLoading} isError={hasEarningError}>
      <VehicleTransactionForm
        data-testid={props['data-testid']}
        values={{
          name: earning?.name ?? '',
          realPrice: earning?.realPrice ?? null,
          expectedPrice: earning?.expectedPrice ?? null,
          note: earning?.note ?? null,
          fileId: earning?.fileId ?? null,
          category: earning?.category ? [earning.category] : null,
          transactionType: ['ONE_TIME'],
          calculationType: null,
          startDate: null,
          endDate: null,
          interestPA: null,
        }}
        onSubmit={onSubmitFullForm}
        onDiscard={props.onDiscard}
        currency={tenantCurrency}
        countryCode={tenantCountry}
        disableRecurring
        canSetExpectedPrice
        categories={earningCategories ?? []}
        showCategories
        labels={{
          categories: i18n.t('entity.vehicle.earning.categories'),
        }}
        ref={formRef}
      />
    </DataStatus>
  );
}

function transformToOneTimePrice(price: VatCodePrice | null | undefined): OneTimePrice | null {
  if (!price || price.withoutVat?.amount === null) {
    return null;
  }

  if (!price.vatDirection || !price.vatRate || !price.vatType) {
    return null;
  }

  return {
    vatType: price.vatType,
    vatRate: price.vatRate,
    withoutVat: price.withoutVat,
    withVat: price.withVat,
    vatDirection: price.vatDirection,
  } as OneTimePrice;
}
